/**
 * Build a dot-notation translation key for a given count, following
 * the Rails `one` / `other` pluralization convention.
 *
 * Given the yaml:
 *   bulk_bar:
 *     products_selected:
 *       one: "%{count} product selected"
 *       other: "%{count} products selected"
 *
 * `pluralizeKey('bulk_bar.products_selected', 1)` returns
 * `'bulk_bar.products_selected.one'` — pass the result to `t()`.
 *
 * Both branches keep the `%{count}` token so downstream interpolation
 * (CountedText / AnimatedCount in the bulk bars, or the dialog title's
 * simple string replace) works identically for either count. The
 * frontend `t()` does not pluralize on its own; callers pick the leaf
 * explicitly.
 */
export const pluralizeKey = (baseKey: string, count: number): string =>
  `${baseKey}.${count === 1 ? 'one' : 'other'}`;

/**
 * Replace the Rails-style `%{count}` token in a resolved translation
 * string with a concrete number. The frontend `t()` does not interpolate
 * `%{…}` tokens on its own — callers substitute at the use site.
 *
 * Example:
 *   interpolateCount('%{count} products selected', 9)  // '9 products selected'
 */
const COUNT_TOKEN = '%{count}';

export const interpolateCount = (template: string, count: number): string =>
  template.replace(COUNT_TOKEN, String(count));
